import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { apiErrorDetail } from '@/lib/api/fraud-client'
import { useFraudPlatform, useUpdateFraudPlatform } from '@/lib/api/fraud-queries'
import type { FraudMode, FraudPlatformResponse, FraudProfile } from '@/lib/api/fraud-types'
import { cn } from '@/lib/utils'
import { Banner, ModeChip, Spinner } from '../shared'
import { MODE_META, fmtDateTime } from '../format'

const FIELD = 'w-full rounded-md border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-sm'

/**
 * The rollout switch: the mode every event follows unless it overrides it,
 * and the profile a new event starts on. One row, platform-wide; every
 * change is kept in history with who made it.
 */
export function PlatformCard() {
  const { data, isLoading, error } = useFraudPlatform()
  if (error) return <Banner kind="error">Failed to load the platform switch: {error.message}</Banner>
  if (isLoading || !data) return <div className="flex items-center justify-center py-10"><Spinner /></div>
  // Remount on save so the form's initial state is always the saved row.
  return <PlatformForm key={data.settings.updated_at} data={data} />
}

function PlatformForm({ data }: { data: FraudPlatformResponse }) {
  const update = useUpdateFraudPlatform()
  const [mode, setMode] = useState<FraudMode>(data.settings.mode)
  const [profile, setProfile] = useState<FraudProfile>(data.settings.default_profile)
  const [error, setError] = useState<string | null>(null)
  const dirty = mode !== data.settings.mode || profile !== data.settings.default_profile

  const apply = async () => {
    setError(null)
    try {
      await update.mutateAsync({ mode, default_profile: profile })
    } catch (err) {
      setError(apiErrorDetail(err, 'Could not update the platform switch.'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-3">
          Platform switch <ModeChip mode={data.settings.mode} />
          <span className="text-xs font-normal text-neutral-500">every event follows this unless it sets its own mode</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Banner kind="error">{error}</Banner>}
        {!data.evervault_configured && (
          <Banner kind="info">Evervault credentials are not configured on the platform, so Enforce is refused: no 3-D Secure challenge could be satisfied.</Banner>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {data.modes.map((m) => {
            const meta = MODE_META[m]
            const selected = mode === m
            const disabled = m === 'enforce' && !data.evervault_configured
            return (
              <label
                key={m}
                className={cn('flex flex-col gap-1 p-3 rounded-lg border text-sm',
                  selected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-neutral-200 dark:border-neutral-800',
                  disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
              >
                <span className="flex items-center gap-2 font-medium text-neutral-900 dark:text-white">
                  <input type="radio" name="platform-mode" value={m} checked={selected} disabled={disabled} onChange={() => setMode(m)} />
                  {meta.label}
                </span>
                <span className="text-xs text-neutral-600 dark:text-neutral-400">{meta.description}</span>
              </label>
            )
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <label className="text-sm">
            <span className="block mb-1 font-medium text-neutral-800 dark:text-neutral-200">Profile for new events</span>
            <select className={FIELD} value={profile} onChange={(e) => setProfile(e.target.value as FraudProfile)}>
              {data.profiles.filter((p) => p.value !== 'custom').map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <span className="block mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {data.profiles.find((p) => p.value === profile)?.description}
            </span>
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={apply} disabled={!dirty || update.isPending}>
              {update.isPending ? 'Applying…' : dirty ? `Apply${mode !== data.settings.mode ? ` · ${MODE_META[mode].label}` : ''}` : 'Up to date'}
            </Button>
            {dirty && (
              <Button variant="outline" onClick={() => { setMode(data.settings.mode); setProfile(data.settings.default_profile) }} disabled={update.isPending}>
                Reset
              </Button>
            )}
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Last changed {fmtDateTime(data.settings.updated_at)}. Takes effect within 30 seconds; an event with its own mode is unaffected.
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
